import { Directive, HostListener, Input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

/*
  Generic input pattern directive
  Usage:
    <input appPattern="mobile10" />           // only 10 digits
    <input appPattern="mobile8to15" />        // 8-15 digits
    <input appPattern="alphaSpaces" />        // letters and spaces only
    <input [appPattern]="/^[0-9]{6,12}$/" />  // custom regex (RegExp)
*/
@Directive({
  selector: '[appPattern]',
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: InputPatternDirective, multi: true }]
})
export class InputPatternDirective implements Validator {
  @Input('appPattern') pattern?: string | RegExp;

  private readonly presets: Record<string, RegExp> = {
    mobile10: /^[0-9]{10}$/,
    mobile8to15: /^[0-9]{8,15}$/,
    alphaSpaces: /^[A-Za-z ]+$/
  };

  // Prevent invalid characters for numeric-only presets
  @HostListener('keypress', ['$event'])
  onKeyPress(e: KeyboardEvent) {
    if (!this.pattern) return;
    const key = e.key;
    const pat = this.resolvePattern(this.pattern);
    if (this.isNumericPreset(this.pattern)) {
      if (!/\d/.test(key)) {
        // allow control keys
        if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
          e.preventDefault();
        }
      }
    } else if (this.pattern === 'alphaSpaces') {
      if (!/[A-Za-z ]/.test(key)) {
        if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
          e.preventDefault();
        }
      }
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value as string;
    if (val == null || val === '') return null;
    const regex = this.resolvePattern(this.pattern);
    if (!regex) return null;
    return regex.test(val) ? null : { appPattern: true };
  }

  private resolvePattern(p?: string | RegExp): RegExp | null {
    if (!p) return null;
    if (p instanceof RegExp) return p;
    const preset = this.presets[p];
    if (preset) return preset;
    try {
      return new RegExp(p);
    } catch {
      return null;
    }
  }

  private isNumericPreset(p?: string | RegExp): boolean {
    return typeof p === 'string' && (p === 'mobile10' || p === 'mobile8to15');
  }
}
